// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { setRememberMeToken } from 'utils/local-storage';
import { setAppLanguage } from 'utils/i18n';
import { PRIVATE, ADMIN } from 'router/routes';
import { useSetToken, useUser } from 'utils/contexts/app-context';
import { canManageAccountSettings } from 'utils/accountSettingsPermissions';
import { clearAll } from 'state/ducks/global';
import ImagePlaceHolder from 'components/image-placeholder';
import { Flags } from 'providers/FlagsProvider';
import FeatureFlags from 'utils/feature-flags';
import styles from './styles.module.scss';
import {
  Avatar,
  Box,
  Collapse,
  Divider,
  Menu,
  MenuItem,
  MenuList,
  Switch,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TuneIcon from '@mui/icons-material/Tune';
import SyncIcon from '@mui/icons-material/Sync';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import { useDarkMode } from 'providers/DarkModeProvider';
import { Auth } from 'aws-amplify';
import ContactUsDialog, { getHelpLegalUrls } from 'components/HelpMenu/ContactUsDialog';

type Props = {
  picture: ?string,
  clearState: () => void,
};

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
];

const ProfilePictureWithDropdown = ({
  picture,
  clearState,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const setToken = useSetToken();
  const user = useUser();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [contactOpen, setContactOpen] = React.useState(false);
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { toggleDarkMode } = useDarkMode();
  const canManageSettings = canManageAccountSettings(user);
  const [languageOpen, setLanguageOpen] = React.useState(false);
  const languageSectionRef = React.useRef(null);
  const { termsUrl, privacyUrl } = getHelpLegalUrls(i18n.language);

  const currentLanguage = i18n.language?.includes('fr') ? 'fr' : 'en';
  const currentLanguageLabel =
    LANGUAGE_OPTIONS.find((option) => option.code === currentLanguage)?.label ??
    'English';

  const isOpen = Boolean(anchorEl);
  const displayName = user?.firstName?.trim() || '';
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();

  const settingsNavItems = React.useMemo(() => {
    const items = [
      { label: t('nouns.profile'), path: PRIVATE.profile, icon: PersonOutlineIcon },
    ];
    if (canManageSettings) {
      items.push(
        { label: t('nouns.validation_plural'), path: PRIVATE.profileValidations, icon: TuneIcon },
        { label: t('nouns.live_validation'), path: PRIVATE.profileLiveUpdates, icon: SyncIcon },
      );
    }
    items.push({
      label: t('nouns.security_settings'),
      path: PRIVATE.profileSecurity,
      icon: LockOutlinedIcon,
    });
    return items;
  }, [canManageSettings, t]);

  const isSettingsNavActive = (path) => {
    if (path === PRIVATE.profile) {
      return location.pathname === PRIVATE.profile;
    }
    return location.pathname === path;
  };

  const showUserManagement = user && (user.isAdmin || user.accessLevel === 'LEAD');
  const showPlans = user && user.isAdmin;
  const showIntegrations = user && user.isAdmin;
  const showWorkspaceSection =
    canManageSettings || showUserManagement || showPlans || showIntegrations;

  const renderSectionHeader = (label) => (
    <Typography
      component="div"
      sx={{
        px: 2,
        pt: 1,
        pb: 0.5,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
  );

  const renderNavItem = (item) => {
    if (!item) return null;
    const Icon = item.icon;
    return (
      <MenuItem
        key={item.path}
        selected={isSettingsNavActive(item.path)}
        onClick={() => goTo(item.path)}
        sx={menuItemSx}
      >
        <Icon fontSize="small" />
        {item.label}
      </MenuItem>
    );
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setLanguageOpen(false);
  };

  const goTo = (path) => {
    navigate(path);
    handleClose();
  };

  const onUsersClick = () => {
    if (user.accessLevel === 'LEAD') {
      goTo(`/admin/team/${user.team_id}`);
    } else {
      goTo(ADMIN.delegates);
    }
  };

  const onLogout = async () => {
    try {
      await Auth.signOut({ global: true });
    } catch (err) {
      console.log('Cognito signOut not needed or failed:', err);
    }

    setRememberMeToken(null);
    setToken(null);
    clearState();
    localStorage.removeItem('filtering');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    sessionStorage.setItem('justLoggedOut', 'true');

    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    handleClose();
    window.location.href = '/login';
  };

  const onSelectLanguage = (languageCode) => {
    void setAppLanguage(languageCode);
    setLanguageOpen(false);
  };

  const onToggleLanguageOpen = () => {
    setLanguageOpen((open) => {
      const nextOpen = !open;
      if (nextOpen) {
        requestAnimationFrame(() => {
          languageSectionRef.current?.scrollIntoView({ block: 'nearest' });
        });
      }
      return nextOpen;
    });
  };

  const menuPaperSx = {
    width: 280,
    maxWidth: 'calc(100vw - 24px)',
    maxHeight: 'calc(100vh - 24px)',
    mt: 1,
    borderRadius: 2,
    overflowY: 'auto',
    overflowX: 'hidden',
  };

  const menuItemSx = {
    py: 1,
    px: 2,
    gap: 1.5,
    fontSize: 14,
    textTransform: 'none',
    '& .MuiSvgIcon-root': {
      fontSize: 20,
      color: 'text.secondary',
    },
    '&.Mui-selected': {
      bgcolor: theme.palette.mode === 'dark' ? 'rgba(100, 181, 246, 0.12)' : 'rgba(25, 118, 210, 0.08)',
      fontWeight: 600,
      '& .MuiSvgIcon-root': {
        color: 'primary.main',
      },
    },
    '&.Mui-selected:hover': {
      bgcolor: theme.palette.mode === 'dark' ? 'rgba(100, 181, 246, 0.18)' : 'rgba(25, 118, 210, 0.12)',
    },
  };

  const preferenceItemSx = {
    ...menuItemSx,
    justifyContent: 'space-between',
  };

  const languageOptionSx = {
    ...menuItemSx,
    pl: 3.5,
    gap: 1,
  };

  const languageDotSx = {
    width: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  return (
    <>
      <div className={styles.profile_picture_holder}>
        <div
          className={styles.profile_trigger}
          onClick={handleOpen}
          id="account_settings_circle"
          role="button"
          tabIndex={0}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleOpen(event);
            }
          }}
        >
          <div className={styles.image_wrapper}>
            <ImagePlaceHolder src={picture} />
          </div>
          {displayName && (
            <span className={styles.profile_name}>{displayName}</span>
          )}
        </div>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: menuPaperSx,
            elevation: 8,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={picture || undefined}
            alt=""
            sx={{ width: 44, height: 44, bgcolor: 'primary.light' }}
          >
            {(user?.firstName?.[0] || user?.email?.[0] || '?').toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {fullName && (
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {fullName}
              </Typography>
            )}
            {user?.email && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {user.email}
              </Typography>
            )}
            {user?.company?.name && (
              <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mt: 0.25 }}>
                {user.company.name}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider />

        {renderSectionHeader(t('nouns.profile_menu_account'))}
        <MenuList dense disablePadding sx={{ py: 0.5 }}>
          {renderNavItem(settingsNavItems.find((item) => item.path === PRIVATE.profile))}
          {renderNavItem(settingsNavItems.find((item) => item.path === PRIVATE.profileSecurity))}
        </MenuList>

        {showWorkspaceSection && (
          <>
            <Divider sx={{ my: 0.5 }} />
            {renderSectionHeader(t('nouns.profile_menu_workspace'))}
            <MenuList dense disablePadding sx={{ py: 0.5 }}>
              {canManageSettings &&
                renderNavItem(settingsNavItems.find((item) => item.path === PRIVATE.profileValidations))}
              {canManageSettings &&
                renderNavItem(settingsNavItems.find((item) => item.path === PRIVATE.profileLiveUpdates))}

              {showUserManagement && (
                <MenuItem onClick={onUsersClick} sx={menuItemSx}>
                  <GroupOutlinedIcon fontSize="small" />
                  {t('nouns.user_management')}
                </MenuItem>
              )}

              <Flags authorizedFlags={[FeatureFlags.AllowBilling]}>
                {showPlans && (
                  <MenuItem onClick={() => goTo(ADMIN.plans)} sx={menuItemSx}>
                    <PaymentOutlinedIcon fontSize="small" />
                    {t('nouns.plans_and_payments')}
                  </MenuItem>
                )}
              </Flags>

              <Flags authorizedFlags={[FeatureFlags.AllowIntegrations]}>
                {showIntegrations && (
                  <MenuItem onClick={() => goTo(ADMIN.integrations)} sx={menuItemSx}>
                    <ExtensionOutlinedIcon fontSize="small" />
                    {t('nouns.integrations')}
                  </MenuItem>
                )}
              </Flags>

              {canManageSettings && (
                <MenuItem
                  selected={location.pathname === PRIVATE.usage}
                  onClick={() => goTo(PRIVATE.usage)}
                  sx={menuItemSx}
                >
                  <BarChartOutlinedIcon fontSize="small" />
                  {t('nouns.usage_and_credits')}
                </MenuItem>
              )}
            </MenuList>
          </>
        )}

        <Divider sx={{ my: 0.5 }} />

        {renderSectionHeader(t('nouns.profile_menu_preferences'))}
        <MenuList dense disablePadding sx={{ py: 0.5 }} ref={languageSectionRef}>
          <MenuItem
            onClick={onToggleLanguageOpen}
            sx={preferenceItemSx}
            aria-expanded={languageOpen}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <LanguageOutlinedIcon fontSize="small" />
              {currentLanguageLabel}
            </Box>
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                color: 'text.secondary',
                flexShrink: 0,
                transform: languageOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </MenuItem>
          <Collapse in={languageOpen} timeout="auto" unmountOnExit>
            {LANGUAGE_OPTIONS.map((option) => (
              <MenuItem
                key={option.code}
                selected={currentLanguage === option.code}
                onClick={() => onSelectLanguage(option.code)}
                sx={languageOptionSx}
              >
                <Box sx={languageDotSx}>
                  {currentLanguage === option.code && (
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                      }}
                    />
                  )}
                </Box>
                {option.label}
              </MenuItem>
            ))}
          </Collapse>

          <MenuItem
            sx={preferenceItemSx}
            disableRipple
            onClick={(event) => event.stopPropagation()}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <DarkModeOutlinedIcon fontSize="small" />
              {t('nouns.dark_mode')}
            </Box>
            <Switch
              size="small"
              checked={theme.palette.mode === 'dark'}
              onChange={() => toggleDarkMode()}
              onClick={(event) => event.stopPropagation()}
              inputProps={{ 'aria-label': t('nouns.dark_mode') }}
            />
          </MenuItem>
        </MenuList>

        <Divider />

        <MenuList dense disablePadding sx={{ py: 0.5 }}>
          <MenuItem
            onClick={() => {
              handleClose();
              setContactOpen(true);
            }}
            sx={menuItemSx}
          >
            <MailOutlineIcon fontSize="small" />
            {t('sentences:help_menu.contact_us')}
          </MenuItem>
          <MenuItem
            component="a"
            href={termsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClose}
            sx={menuItemSx}
          >
            <DescriptionOutlinedIcon fontSize="small" />
            {t('sentences:help_menu.terms')}
          </MenuItem>
          <MenuItem
            component="a"
            href={privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClose}
            sx={menuItemSx}
          >
            <PolicyOutlinedIcon fontSize="small" />
            {t('sentences:help_menu.privacy')}
          </MenuItem>
        </MenuList>

        <Divider />

        <MenuList dense disablePadding>
          <MenuItem
            onClick={onLogout}
            sx={{
              ...menuItemSx,
              color: 'error.main',
              '& .MuiSvgIcon-root': { color: 'error.main' },
            }}
          >
            <LogoutOutlinedIcon fontSize="small" />
            {t('verbs.logout')}
          </MenuItem>
        </MenuList>
      </Menu>
      <ContactUsDialog open={contactOpen} onClose={() => setContactOpen(false)} />
    </>
  );
};

const mapDispatchToProps = dispatch => ({
  clearState() {
    dispatch(clearAll());
  },
});

const ConnectedComponent = connect(
  null,
  mapDispatchToProps
)(ProfilePictureWithDropdown);

export default ConnectedComponent;
